import "@/index.css";
import "@/App.css";
import "./components/qelli/qelli.css";
import "./pages/help-center.css";
import React, { useEffect } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import i18n, { resolveLocaleFromPath } from "./i18n";
import { AuthProvider } from "./lib/auth";
import { UserAuthProvider } from "./lib/userAuth";
import { BrandProvider } from "./lib/brand";
import { ConfirmModalProvider } from "./components/ConfirmModal";
import { TeamRoleProvider } from "./lib/teamRoles";
import { ConsentProvider } from "./lib/consent";
import { initPostHog } from "./lib/posthog";
import RouteTracker from "./components/RouteTracker";
import ScrollToTop from "./components/ScrollToTop";
import LabsHostRedirect from "./components/LabsHostRedirect";
import CookieBanner from "./components/CookieBanner";
import PersonaPromptModal from "./components/PersonaPromptModal";
import QelliWidget from "./components/qelli/QelliWidget";
import QelliAvatar3D from "./components/qelli/QelliAvatar3D";
import LanguageSuggestionToast from "./components/LanguageSuggestionToast";
import HreflangTags from "./components/HreflangTags";
import SitePostureBanner from "./components/SitePostureBanner";
import ReferralAttribution from "./components/ReferralAttribution";
import BackToTopButton from "./components/BackToTopButton";
import ReadingProgressBar from "./components/ReadingProgressBar";
import HeadingAnchorLinks from "./components/HeadingAnchorLinks";
import DocFeedbackMounter from "./components/DocFeedbackWidget";
import AppRoutes from "./routes";

// (legacy QelliFloatingPill / QelliFloatingPillForVisitors retired —
// QelliAvatar3D now serves both authed + un-authed visitors globally.)

// M3a — QorBOM sister-brand routes shouldn't render any QorTrace
// global chrome (Qelli avatar, language toast, posture banner). Wrap
// the QorTrace-branded globals in a path-aware gate so /qorbom/* is
// pristinely QorBOM.
function QorTraceOnlyChrome() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/qorbom")) return null;
  // Iter269.20m — Deck is a self-contained presentation surface. Suppress
  // the global chrome (cookie banner, Qelli widget, etc.) so investors/
  // partners see a clean, focused screen they can fullscreen + share.
  if (pathname.startsWith("/deck")) return null;
  return (
    <>
      <ReadingProgressBar />
      <HeadingAnchorLinks />
      <DocFeedbackMounter />
      <SitePostureBanner />
      <PersonaPromptModal />
      <QelliWidget />
      <QelliAvatar3D />
      <BackToTopButton />
      <LanguageSuggestionToast />
    </>
  );
}

function App() {
  useEffect(() => {
    // Idempotent — and consent-gated. Will register a one-shot listener
    // that re-attempts init the moment the user grants analytics consent.
    initPostHog();
  }, []);

  // Iter250 — resolve `/de`, `/fr`, `/es`, `/ja` URL prefix to a basename
  // for BrowserRouter. English routes stay at the root (no prefix) so we
  // don't break any pre-existing canonical URLs / inbound links.
  const { lang, basename } = resolveLocaleFromPath();
  if (lang) {
    // Eagerly set i18next so the first paint is in the right locale.
    // The HttpBackend `loadPath` will fetch the dictionary in parallel
    // with React mounting; useSuspense:false makes that smooth.
    if (i18n.resolvedLanguage !== lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }

  return (
    <BrowserRouter basename={basename}>
      <ConsentProvider>
        <BrandProvider>
          <UserAuthProvider>
            <AuthProvider>
              <TeamRoleProvider>
                <ConfirmModalProvider>
                  <RouteTracker />
                  <ScrollToTop />
                  <ReferralAttribution />
                  <LabsHostRedirect />
                  <HreflangTags />
                  <AppRoutes />
                  <CookieBanner />
                  <QorTraceOnlyChrome />
                </ConfirmModalProvider>
              </TeamRoleProvider>
            </AuthProvider>
          </UserAuthProvider>
        </BrandProvider>
      </ConsentProvider>
    </BrowserRouter>
  );
}

export default App;
